import { useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';

interface UseDebouncedSearchOptions {
  debounceDelay?: number;
  allowEmptySearch?: boolean;
}

interface UseDebouncedSearchReturn {
  search: (query: string) => void;
  cancel: () => void;
}
export const useDebouncedSearch = (
  searchFunction: (query: string, abortSignal: AbortSignal) => void | Promise<void>,
  options: UseDebouncedSearchOptions = {},
): UseDebouncedSearchReturn => {
  const { debounceDelay = 400, allowEmptySearch = false } = options;

  // Ref to store the current abort controller
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cancel any ongoing request
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      // Cancel any previous request
      cancel();

      // If query is empty and empty search is not allowed, return
      if (query.length === 0 && !allowEmptySearch) {
        return;
      }

      // If query is empty and empty search is allowed, proceed
      if (query.length === 0 && allowEmptySearch) {
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        // Execute search function
        searchFunction(query, abortControllerRef.current.signal);
        return;
      }

      // Check minimum characters based on query type
      const hasLetters = /[a-zA-Z]/.test(query);
      const minRequiredLength = hasLetters ? 2 : 1;

      if (query.length < minRequiredLength) {
        return;
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      // Execute search function
      searchFunction(query, abortControllerRef.current.signal);
    }, debounceDelay),
    [searchFunction, debounceDelay, cancel, allowEmptySearch],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
      debouncedSearch.cancel();
    };
  }, [cancel, debouncedSearch]);

  return {
    search: debouncedSearch,
    cancel,
  };
};
